import { FC, ChangeEvent, useMemo } from 'react';
import { useStore } from 'hooks/useRootStore';
import { makeStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TreeItem from '@material-ui/lab/TreeItem';
import { TreeItem as RenderTree } from 'utils';
import { observer } from 'mobx-react-lite';
import { blue } from '@material-ui/core/colors';

const useStyles = makeStyles(
  {
    root: {
      display: 'flex',
      flexGrow: 1,
      '& .MuiTreeItem-root.Mui-selected': {
        '& > .MuiTreeItem-content .MuiTreeItem-label': {
          backgroundColor: blue[100],
        },
      },
    },
  },
  {
    index: 1,
  }
);

export const BaseTreeExplorer: FC = observer(() => {
  const classes = useStyles();
  const {
    baseTreeExplorerStore: { baseTree, selected, expanded, setSelected, setExpanded },
  } = useStore();

  // Sanitize tree data to prevent circular references
  const sanitizedTree = useMemo(() => {
    if (!baseTree) return null;

    const sanitizeNode = (node: RenderTree, visitedIds = new Set<string>(), depth = 0): RenderTree | null => {
      // Prevent infinite depth
      if (depth > 20) {
        console.warn('Tree depth exceeded maximum limit at node:', node.id);
        return null;
      }

      // Prevent circular references
      if (visitedIds.has(node.id)) {
        console.warn('Circular reference detected for node:', node.id);
        return { ...node, children: [] }; // Return node without children to break cycle
      }

      const newVisitedIds = new Set(visitedIds);
      newVisitedIds.add(node.id);

      const sanitizedChildren = [];
      if (Array.isArray(node.children)) {
        for (const child of node.children) {
          const sanitizedChild = sanitizeNode(child, newVisitedIds, depth + 1);
          if (sanitizedChild) {
            sanitizedChildren.push(sanitizedChild);
          }
        }
      }

      return {
        ...node,
        children: sanitizedChildren,
      };
    };

    return sanitizeNode(baseTree);
  }, [baseTree]);

  const renderTree = (node: RenderTree): JSX.Element => {
    return (
      <TreeItem key={node.id} nodeId={node.id} label={node.name}>
        {node.children && node.children.length > 0 ? node.children.map((child) => renderTree(child)) : null}
      </TreeItem>
    );
  };

  const handleOnNodeSelect = (event: ChangeEvent<unknown>, nodeIds: string) => {
    setSelected(nodeIds);
  };

  const handleonNodeToggle = (event: ChangeEvent<unknown>, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  if (!sanitizedTree) {
    return null;
  }

  return (
    <TreeView
      className={classes.root}
      onNodeToggle={handleonNodeToggle}
      onNodeSelect={handleOnNodeSelect}
      defaultExpanded={expanded}
      selected={selected}
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpandIcon={<ChevronRightIcon />}
    >
      {renderTree(sanitizedTree)}
    </TreeView>
  );
});
